import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { type Message } from "@lr-notitia/notitia-alternis/schemas";
import { api, apiConfig as operatorAPIConfig } from "@lr-notitia/notitia-operator/client";
import { useGraphStore } from "../graph";
import { useDomainStore } from "../domain";
import { useSymbolsStore } from "../symbols";
import { useConfigStore } from "../alternis/config";
import { useInstanceWorkspacesStore } from "../i-workspace";
import { useInstanceDataspacesStore } from "../i-dataspace";
import { Question } from "../questions";
import { useAlternisNewStore } from "../alternis_new";

type ContentEnum = {
	name: string;
	description: string;
	id: string;
	synonyms?: string[];
};
type ContentAttribute = {
	name: string;
	description: string;
	id: string;
	expectedResponse: "text generation" | "enum.id";
	enums?: ContentEnum[];
};
type Content = {
	chat: string[];
	question_to_answer: string;
	attributes: ContentAttribute[];
};
type Suggestions = Record<string, string>;

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
let configStore: ReturnType<typeof useConfigStore>;
let workspacesStore: ReturnType<typeof useInstanceWorkspacesStore>;
let dataspaceStore: ReturnType<typeof useInstanceDataspacesStore>;
let alternisNewStore: ReturnType<typeof useAlternisNewStore>;

function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	symbolsStore = useSymbolsStore();
	configStore = useConfigStore();
	workspacesStore = useInstanceWorkspacesStore();
	dataspaceStore = useInstanceDataspacesStore();
	alternisNewStore = useAlternisNewStore();
}

export const useGptSuggestionsStore = defineStore("gpt-suggestions", () => {
	const suggestionData = ref<Suggestions>({});
	const currentlyProcessing = ref<number>(0);

	function getLocalISOString(): string {
		const now = new Date();
		const offsetMinutes = now.getTimezoneOffset(); // e.g. -120 for UTC+2
		const sign = offsetMinutes <= 0 ? "+" : "-";
		const absOffset = Math.abs(offsetMinutes);
		const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
		const minutes = String(absOffset % 60).padStart(2, "0");
		const local = new Date(now.getTime() - offsetMinutes * 60_000);
		return local.toISOString().replace("Z", `${sign}${hours}:${minutes}`);
	}

	const longToShort: { [longId: string]: string } = {};
	const shortToLong: { [shortId: string]: string } = {};
	let idCounter = 0;
	function getShortId(longId: string) {
		if (longToShort[longId]) return longToShort[longId];
		const shortId = "ID" + idCounter++;
		longToShort[longId] = shortId;
		shortToLong[shortId] = longId;
		return shortId;
	}
	function getLongId(shortId: string) {
		return shortToLong[shortId];
	}
	function isShortId(s: string) {
		return s.startsWith("ID") && !isNaN(parseInt(s.slice(2)));
	}

	function improveGptResponse(gptRes: string): string {
		// remove all characters before the first "{" and after the last "}"
		let start = gptRes.indexOf("{");
		let end = gptRes.lastIndexOf("}");
		if (start === -1 || end === -1) return gptRes;
		return gptRes.slice(start, end + 1);
	}

	async function askLLM(chatMessages: Message[], question: Question) {
		if (!graphStore || !domainStore || !configStore) init();

		let content: Content = {
			chat: [],
			// TODO not ideal to hardcode this to "de", but using languagePrefence is also not the ideal solution,
			// we have to come up with something more robust
			question_to_answer: question.title["de"] + " - " + question.subtitle["de"],
			attributes: [],
		};
		content.chat = chatMessages.map((msg) => {
			msg = msg as Message;
			// text domain lang
			if (msg.lang === domainStore.languagePreference) return msg.words.map((w) => w.value).join(" ");
			// translation domain lang
			else if (msg.translation && msg.translation[domainStore.languagePreference])
				return msg.translation[domainStore.languagePreference].value;
			// text engl
			else if (msg.lang === "en") return msg.words.map((w) => w.value).join(" ");
			// translation engl
			else if (msg.translation && msg.translation["en"]) return msg.translation["en"].value;
			// text any
			else return msg.words.map((w) => w.value).join(" ");
		});

		question.instanceTask.outputAttributes.forEach((instanceAttrId) => {
			let instanceAttr = graphStore.instance.attribute.byId(instanceAttrId);
			if (!instanceAttr) return;
			let conceptAttr = graphStore.concept.attribute.byId(instanceAttr.concept);
			if (!conceptAttr) return;

			let name = symbolsStore.term({ id: conceptAttr.refersTo, terminology: conceptAttr.terminology });
			let description = symbolsStore.description({
				id: conceptAttr.refersTo,
				terminology: conceptAttr.terminology,
			});
			if (!name && !description) return;

			let enums: ContentEnum[] = [];
			let arraytype = graphStore.descriptor.datatype.byId(conceptAttr.arraytype);
			let datatype = graphStore.descriptor.datatype.byId(conceptAttr.datatype);
			if (arraytype && arraytype.enumerationItems.length > 0) {
				enums =
					arraytype.enumerationItems.map((enumItemId) => {
						let enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
						let synonyms = symbolsStore.synonyms({
							id: enumItem?.refersTo,
							terminology: enumItem?.terminology,
						});
						return {
							name: symbolsStore.term({
								id: enumItem?.refersTo,
								terminology: enumItem?.terminology,
							}),
							description: symbolsStore.description({
								id: enumItem?.refersTo,
								terminology: enumItem?.terminology,
							}),
							...(synonyms && synonyms.length > 0 && { synonyms }),
							id: getShortId(enumItemId),
						};
					}) || [];
			} else if (datatype && datatype.enumerationItems.length > 0) {
				enums =
					datatype.enumerationItems.map((enumItemId) => {
						let enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
						let synonyms = symbolsStore.synonyms({
							id: enumItem?.refersTo,
							terminology: enumItem?.terminology,
						});
						return {
							name: symbolsStore.term({
								id: enumItem?.refersTo,
								terminology: enumItem?.terminology,
							}),
							description: symbolsStore.description({
								id: enumItem?.refersTo,
								terminology: enumItem?.terminology,
							}),
							...(synonyms && synonyms.length > 0 && { synonyms }),
							id: getShortId(enumItemId),
						};
					}) || [];
			}

			// TEMPORARYY: if more than 10 enums, only use the best fitting 10 enums
			// to achieve this, we sort the enums by a similarity score to the chat
			// and then take the first 10
			if (enums.length > 10) {
				enums = enums
					.map((enumItem) => {
						const nameScore = enumItem.name
							.toLowerCase()
							.split(" ")
							.reduce(
								(acc, word) => (content.chat.join(" ").toLowerCase().includes(word) ? acc + 1 : acc),
								0,
							);

						const synonymsScore = enumItem.synonyms
							?.map((synonym) =>
								synonym
									.toLowerCase()
									.split(" ")
									.reduce(
										(acc, word) =>
											content.chat.join(" ").toLowerCase().includes(word) ? acc + 1 : acc,
										0,
									),
							)
							.reduce((acc, score) => acc + score, 0);

						return {
							...enumItem,
							score: nameScore + (synonymsScore || 0),
						};
					})
					.sort((a, b) => b.score - a.score)
					.slice(0, 10)
					.map(({ score, ...rest }) => rest); // Remove the score property
			}
			// END TEMPORARY

			content.attributes.push({
				name,
				description,
				id: getShortId(instanceAttrId),
				expectedResponse: enums.length > 0 ? "enum.id" : "text generation",
				...(enums.length > 0 && { enums }),
			});
		});

		if (content.attributes.length === 0) return;

		let systemMessage = SYSTEM_PROMPT;

		let userContent = JSON.stringify(content);

		let messages = [
			{
				role: "user",
				content: userContent,
			},
		];

		messages.unshift({
			role: "system",
			content: systemMessage,
		});

		currentlyProcessing.value += 1;
		api()
			.gpt()
			.post({
				model: configStore.getSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || ""),
				messages: messages,
				internal_type: "suggestions (client side)",
				temperature: 1,
				jsonMode: false,
			})
			.then((r) => {
				let gptRes = r;
				gptRes = improveGptResponse(gptRes);
				try {
					let answerJSON: { [key: string]: string } = JSON.parse(gptRes); // example: { "ID1": "generated text", "ID2": "ID3", "ID3": "?"}
					if (typeof answerJSON !== "object") throw new Error("GPT Answer is not an object");
					Object.keys(answerJSON).forEach((key) => {
						if (typeof answerJSON[key] !== "string") throw new Error("GPT Answer key is not a string");
					});
					Object.values(answerJSON).forEach((val) => {
						if (typeof val !== "string") throw new Error("GPT Answer value is not a string");
					});

					Object.entries(answerJSON).forEach(([key, val]) => {
						if (!val) return;
						if (val === "?") return;
						suggestionData.value[getLongId(key)] = isShortId(val) ? getLongId(val) : val;
					});
				} catch (error) {
					console.error("Error parsing GPT response: ", error);
				}
			})
			.catch((err) => console.warn(err))
			.finally(() => (currentlyProcessing.value -= 1));
	}

	// New Alternis (server side)
	let eventSource: EventSource | null = null;
	function initSSE() {
		if (!alternisNewStore || !workspacesStore) init();
		if (!workspacesStore.openedWorkspaceId) return;
		closeSSE(); // close any existing connection to avoid duplicate entries on the server
		let endpoint = operatorAPIConfig.host;
		endpoint += "/sseSuggestions";
		const url = new URL(endpoint, window.location.href);
		url.searchParams.set("domainId", domainStore.domain.id);
		url.searchParams.set("workspaceId", workspacesStore.openedWorkspaceId);
		url.searchParams.set(
			"suggestionModel",
			configStore.getSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || ""),
		);
		eventSource = new EventSource(url.toString());

		const currentWorkspaceId = workspacesStore.openedWorkspaceId; // workspaceId at the time of connection

		eventSource.onopen = (e) => {};
		eventSource.onmessage = (e) => {
			// Ignore messages from other workspace sse connections
			if (workspacesStore.openedWorkspaceId !== currentWorkspaceId) return;

			let data = JSON.parse(e.data) as Suggestions;
			for (const key in data) {
				suggestionData.value[key] = data[key];
			}
		};
		eventSource.onerror = (e) => {
			console.log("SSE error", e);
		};
	}
	function closeSSE() {
		if (eventSource) eventSource.close();
	}

	const SYSTEM_PROMPT: string = `
 You are a highly reliable JSON extraction system designed for critical IT applications where accuracy is paramount.
 
 # Two-Step Decision Process
 ## Step 1: Confidence Assessment
 First, assess if you can confidently extract information for each attribute based on explicit evidence in the provided chat. For each attribute, ask:
 - Is there clear, unambiguous evidence in the chat to determine this value?
 - Would another qualified analyst looking at the same data reach the same conclusion?
 - Would you stake your reliability on this extraction being correct?
 
 Only proceed with extraction for attributes where you can answer "yes" to all questions above.
 
 ## Step 2: Precise Extraction
 For attributes that pass Step 1, follow these strict extraction rules:
 
 # Task
 Extract specific <attributes> from a given <chat> based solely on explicit evidence. You will analyze:
 - A list of <attributes> (JSON objects with fields: name, id, description, expectedResponse, and optionally enums)
 - A <chat> (message history as a string array)
 - A <question_to_answer> (provides additional context)
 
 # Response Rules
 1. For enum attributes (expectedResponse = enums.id):
    - Return ONLY a valid enum.id from the provided list
    - Never return enum.name or invent values
    - If uncertain which enum.id applies, exclude the attribute entirely
 
 2. For text generation (expectedResponse = text generation):
    - Generate concise, factual text based solely on chat content
    - Use "YYYY-MM-DD" for dates and "HH:MM" for times
	- Avoid using digit group separators for numbers (e.g. use "1000" not "1,000" or "1.000"), use points for decimals (e.g. "99.50")
 
 # Critical Exclusion Requirements
 - Exclude an attribute if there is any uncertainty about its value
 - Never guess, assume, or infer beyond what is explicitly stated
 - When in doubt, omit the attribute from your response
 - For enum attributes, if you cannot determine with certainty which enum.id applies, exclude it
 
 # Response Format
 Return a valid JSON object with this structure:
 \`\`\`json
 {
   "<attribute.id>": "<enum.id or text generation>"
 }
 \`\`\`
 - Include ONLY attributes you can determine with high confidence
 - An empty JSON object {} is a valid response if no attributes can be confidently determined
 - Output only the JSON without explanations, headers, or commentary
 - To handle relative dates correctly, you need to know the current date and time: ${getLocalISOString()}
 
 # Example
## Input
{
  "chat": ["What is the situation?", "There's a person trapped in a car after an accident here at the Hauptstraße.", "Is the person conscious?", "No, they seem unconscious."],
  "question_to_answer": "Is the person conscious?",
  "attributes": [
    {"name": "streetname", "id": "ID767", "description": "name of the street", "expectedResponse": "text generation"},
    {"name": "consciousness state", "id": "ID469", "description": "state of consciousness", "expectedResponse": "enums.id", "enums": [{"name": "conscious", "id": "ID389"}, {"name": "unconscious", "id": "ID324"}]},
    {"name": "gender", "id": "ID431", "expectedResponse": "enums.id", "enums": [{"name": "male", "id": "ID613"}, {"name": "female", "id": "ID14"}]}, , {"name": "female", "id": "ID154"}]}
  ]
}

## Correct Response
{
  "ID767": "Hauptstraße",
  "ID469": "ID324"
}

Note: The "gender" attribute was excluded there is insufficient explicit evidence to determine the gender of the affected person because of the usage of the pronoun "they" which can be used for all genders.
 `;

	// Reconnect SSE when the suggestion model changes
	const currentModel = computed(() => {
		if (!configStore || !domainStore || !dataspaceStore) return null;
		return configStore.getSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || "");
	});
	watch(currentModel, () => {
		if (eventSource) initSSE();
	});

	return {
		suggestionData,
		askLLM,
		currentlyProcessing,
		initSSE,
		closeSSE,
	};
});
